import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { API } from "@/App";
import { toast } from "sonner";
import {
  Settings, Palette, FileText, ClipboardList, Users, Shield, ToggleLeft,
  Layout, Plus, Trash2, GripVertical, Save, RefreshCw, ChevronRight,
  Type, List, CheckSquare, AlignLeft, Star, Upload, Hash, Mail, Phone,
  ArrowUp, ArrowDown, Copy, Eye, Layers, Image as ImageIcon, Loader2, X,
  Crown, CreditCard, Calendar, Infinity, Zap, Lock, Unlock, Bell,
  MessageSquare, BarChart3, Home, FileSignature, Sparkles, DollarSign,
  Clock, Gift, Percent, AlertCircle, CheckCircle2, Edit2, ChevronDown,
  Play, ArrowRight, Monitor, Smartphone, ChevronLeft, ExternalLink, Globe,
  LayoutDashboard, ListChecks, Info
} from "lucide-react";

// ==================== PLAN CONFIGURATION ====================
const PLAN_OPTIONS = [
  { value: "free", label: "Free", icon: Gift, color: "text-gray-500", description: "Basic access" },
  { value: "basic", label: "Basic", icon: Zap, color: "text-blue-500", description: "$11/mo features" },
  { value: "pro", label: "Pro", icon: Star, color: "text-purple-500", description: "$44/mo features" },
  { value: "luxe", label: "Luxe", icon: Crown, color: "text-amber-500", description: "Full platform access" },
  { value: "custom", label: "Custom", icon: Settings, color: "text-pink-500", description: "Custom configuration" },
];

const DURATION_OPTIONS = [
  { value: "lifetime", label: "Lifetime", icon: Infinity },
  { value: "1_month", label: "1 Month", icon: Calendar },
  { value: "3_months", label: "3 Months", icon: Calendar },
  { value: "6_months", label: "6 Months", icon: Calendar },
  { value: "1_year", label: "1 Year", icon: Calendar },
  { value: "2_years", label: "2 Years", icon: Calendar },
  { value: "custom", label: "Custom End Date", icon: Clock },
];

// Portal visibility options
const PORTAL_SECTIONS = [
  { key: "dashboard", label: "Dashboard Home", icon: Home, description: "Main overview page" },
  { key: "updates", label: "Updates Feed", icon: Bell, description: "Admin announcements" },
  { key: "messages", label: "Messages", icon: MessageSquare, description: "Direct messaging" },
  { key: "analytics", label: "Analytics", icon: BarChart3, description: "Performance metrics" },
  { key: "roadmap", label: "Roadmap", icon: Layout, description: "Product roadmap" },
  { key: "experience", label: "Experience Editor", icon: Sparkles, description: "Customize their profile" },
  { key: "portfolio", label: "Portfolio", icon: ImageIcon, description: "Work showcase" },
  { key: "clients", label: "Client Management", icon: Users, description: "Client CRM" },
  { key: "bookings", label: "Bookings", icon: Calendar, description: "Appointment management" },
  { key: "payments", label: "Payments", icon: DollarSign, description: "Payment links & tips" },
  { key: "settings", label: "Settings", icon: Settings, description: "Account settings" },
];

// Phase templates for invite flow
const INVITE_PHASES = [
  { id: "envelope", label: "Envelope Animation", icon: Mail, description: "Initial reveal animation", step: 1 },
  { id: "welcome", label: "Welcome Page", icon: Home, description: "Introduction & benefits", step: 2 },
  { id: "nda", label: "NDA/Agreement", icon: FileSignature, description: "Legal agreement signing", step: 3 },
  { id: "intake", label: "Intake Form", icon: ClipboardList, description: "Onboarding questions", step: 4 },
  { id: "portal", label: "Portal Dashboard", icon: Layout, description: "Post-onboarding view", step: 5 },
];

// Image Upload Component
function ImageUpload({ label, value, onChange, placeholder, className = "" }) {
  const [uploading, setUploading] = useState(false);
  const [imageError, setImageError] = useState(false);
  const fileInputRef = useRef(null);

  // Reset error when value changes
  useEffect(() => {
    setImageError(false);
  }, [value]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    setUploading(true);
    setImageError(false);
    try {
      const token = localStorage.getItem("session_token");
      const formData = new FormData();
      formData.append("file", file);
      const res = await fetch(`${API}/cms/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      if (res.ok) {
        const data = await res.json();
        console.log("Upload response:", data);
        console.log("Image URL:", data.url);
        console.log("Using Cloudinary:", data.cloudinary);
        
        // Verify the URL is accessible before setting it
        const img = new Image();
        img.onload = () => {
          onChange(data.url);
          toast.success("Image uploaded!");
        };
        img.onerror = () => {
          console.error("Uploaded image URL is not accessible:", data.url);
          toast.error("Image uploaded but failed to load. Check Cloudinary configuration.");
          // Still set the URL so user can see what was saved
          onChange(data.url);
        };
        img.src = data.url;
      } else {
        const errText = await res.text();
        console.error("Upload failed:", errText);
        throw new Error("Upload failed");
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Failed to upload image");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      {label && <Label className="text-xs">{label}</Label>}
      <div className="flex gap-2 items-start">
        {value ? (
          <div className="relative w-20 h-20 rounded-lg overflow-hidden border bg-gray-50 shrink-0">
            {imageError ? (
              <div className="w-full h-full flex flex-col items-center justify-center bg-red-50 text-red-500 p-1">
                <X className="w-4 h-4 mb-1" />
                <span className="text-[8px] text-center leading-tight">Failed to load</span>
              </div>
            ) : (
              <img 
                src={value} 
                alt={label} 
                className="w-full h-full object-cover" 
                onError={() => {
                  console.error("Image failed to load:", value);
                  setImageError(true);
                }}
              />
            )}
            <button
              onClick={() => { onChange(""); setImageError(false); }}
              className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 z-10"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <div
            onClick={() => fileInputRef.current?.click()}
            className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors shrink-0"
          >
            {uploading ? (
              <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
            ) : (
              <>
                <ImageIcon className="w-6 h-6 text-gray-400" />
                <span className="text-[10px] text-gray-400 mt-1">Upload</span>
              </>
            )}
          </div>
        )}
        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
        {value && (
          <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="shrink-0">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          </Button>
        )}
      </div>
    </div>
  );
}

// ==================== PREVIEW AS MEMBER MODAL ====================
function PreviewAsMemberModal({ isOpen, onClose, settings, categoryColors, category, subCategoryId }) {
  // Generate preview URLs - use the correct subCategoryId
  const getPreviewUrl = (phaseId) => {
    const portalId = subCategoryId;
    if (!portalId) return null;
    
    const inviteToken = `preview_${portalId}`;
    const base = window.location.origin;
    
    switch (phaseId) {
      case "envelope":
        // Envelope preview should go to invite page which shows the envelope
        return `${base}/portal/invite/${inviteToken}?preview=true`;
      case "nda":
        return `${base}/pilot/nda/${portalId}?preview=true`;
      case "intake":
        return `${base}/pilot/intake/${portalId}?preview=true`;
      case "portal":
        return `${base}/pilot/confirmation/${portalId}?preview=true`;
      default:
        return null;
    }
  };

  // Remove welcome from phases - users go directly from envelope to NDA
  const phases = [
    { id: "envelope", label: "Invite Page", icon: Mail, description: "Initial invite with envelope animation" },
    { id: "nda", label: "NDA", icon: Shield, description: "Agreement signing" },
    { id: "intake", label: "Intake", icon: ClipboardList, description: "Intake questionnaire" },
    { id: "portal", label: "Confirmation", icon: CheckCircle2, description: "Completion page" },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" /> Member Journey Links
          </DialogTitle>
          <DialogDescription>
            Click any link below to view that stage as a member would see it
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-2 py-4">
          {phases.map((phase) => {
            const Icon = phase.icon;
            const url = getPreviewUrl(phase.id);
            return (
              <a
                key={phase.id}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full ${categoryColors.bg} text-white flex items-center justify-center`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{phase.label}</p>
                    <p className="text-xs text-gray-500">{phase.description}</p>
                  </div>
                </div>
                <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
              </a>
            );
          })}
        </div>
        
        <div className="text-xs text-gray-400 text-center">
          Portal ID: {subCategoryId}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ==================== PLAN SETTINGS SECTION ====================
function PlanSettingsSection({ settings, updateSetting, categoryColors }) {
  const [showCustomDate, setShowCustomDate] = useState(settings.plan_duration === "custom");

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2 mb-1">
          <Crown className="w-5 h-5" /> Plan & Subscription
        </h3>
        <p className="text-sm text-gray-500">Configure the plan tier and duration for all members in this portal</p>
      </div>

      {/* Current Plan Status */}
      <Card className="border-2 border-amber-200 bg-amber-50">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-amber-500 text-white flex items-center justify-center">
                <Crown className="w-6 h-6" />
              </div>
              <div>
                <p className="font-semibold text-lg">{PLAN_OPTIONS.find(p => p.value === (settings.plan_tier || "luxe"))?.label || "Luxe"} Tier</p>
                <p className="text-sm text-amber-700">
                  {settings.plan_duration === "lifetime" || !settings.plan_duration ? "Lifetime Access" : 
                   settings.plan_end_date ? `Expires: ${new Date(settings.plan_end_date).toLocaleDateString()}` : "Lifetime Access"}
                </p>
              </div>
            </div>
            <Badge className="bg-green-500">Active</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Plan Tier Selection */}
      <div>
        <Label className="text-sm font-medium mb-3 block">Plan Tier</Label>
        <div className="grid grid-cols-5 gap-2">
          {PLAN_OPTIONS.map(plan => {
            const Icon = plan.icon;
            const isSelected = (settings.plan_tier || "luxe") === plan.value;
            return (
              <button
                key={plan.value}
                onClick={() => updateSetting("plan_tier", plan.value)}
                className={`p-3 rounded-lg border-2 text-center transition-all ${
                  isSelected 
                    ? `border-purple-500 bg-purple-50 ${categoryColors.text}` 
                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                }`}
                data-testid={`plan-${plan.value}`}
              >
                <Icon className={`w-5 h-5 mx-auto mb-1 ${isSelected ? plan.color : "text-gray-400"}`} />
                <p className="font-medium text-sm">{plan.label}</p>
                <p className="text-xs text-gray-500">{plan.description}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Duration Selection */}
      <div>
        <Label className="text-sm font-medium mb-3 block">Access Duration</Label>
        <div className="grid grid-cols-4 gap-2">
          {DURATION_OPTIONS.slice(0, 4).map(dur => {
            const Icon = dur.icon;
            const isSelected = (settings.plan_duration || "lifetime") === dur.value;
            return (
              <button
                key={dur.value}
                onClick={() => {
                  updateSetting("plan_duration", dur.value);
                  setShowCustomDate(dur.value === "custom");
                }}
                className={`p-3 rounded-lg border-2 text-center transition-all ${
                  isSelected ? "border-purple-500 bg-purple-50" : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <Icon className={`w-4 h-4 mx-auto mb-1 ${isSelected ? "text-purple-600" : "text-gray-400"}`} />
                <p className="text-sm font-medium">{dur.label}</p>
              </button>
            );
          })}
        </div>
        <div className="grid grid-cols-3 gap-2 mt-2">
          {DURATION_OPTIONS.slice(4).map(dur => {
            const Icon = dur.icon;
            const isSelected = (settings.plan_duration || "lifetime") === dur.value;
            return (
              <button
                key={dur.value}
                onClick={() => {
                  updateSetting("plan_duration", dur.value);
                  setShowCustomDate(dur.value === "custom");
                }}
                className={`p-3 rounded-lg border-2 text-center transition-all ${
                  isSelected ? "border-purple-500 bg-purple-50" : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <Icon className={`w-4 h-4 mx-auto mb-1 ${isSelected ? "text-purple-600" : "text-gray-400"}`} />
                <p className="text-sm font-medium">{dur.label}</p>
              </button>
            );
          })}
        </div>
        {showCustomDate && (
          <div className="mt-3">
            <Label className="text-xs">Custom End Date</Label>
            <Input
              type="date"
              value={settings.plan_end_date || ""}
              onChange={(e) => updateSetting("plan_end_date", e.target.value)}
              className="mt-1"
            />
          </div>
        )}
      </div>

      {/* Plan Features */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Included Features</CardTitle>
          <CardDescription>Toggle features available in this plan</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {[
            { key: "nfc_reader_included", label: "NFC Reader Included", desc: "Physical check-in device", comingSoon: true },
            { key: "custom_branding", label: "Custom Branding", desc: "Logo & colors on their portal" },
            { key: "analytics_access", label: "Analytics Dashboard", desc: "Performance metrics & insights" },
            { key: "unlimited_clients", label: "Unlimited Clients", desc: "No limit on client profiles" },
            { key: "priority_support", label: "Priority Support", desc: "Dedicated support channel" },
            { key: "api_access", label: "API Access", desc: "Programmatic integrations" },
          ].map(feature => (
            <div key={feature.key} className="flex items-center justify-between py-1">
              <div className="flex items-center gap-2">
                <div>
                  <p className="text-sm font-medium flex items-center gap-2">
                    {feature.label}
                    {feature.comingSoon && <Badge variant="secondary" className="text-xs">Coming Soon</Badge>}
                  </p>
                  <p className="text-xs text-gray-500">{feature.desc}</p>
                </div>
              </div>
              <Switch
                checked={settings.plan_features?.[feature.key] ?? !feature.comingSoon}
                onCheckedChange={(v) => updateSetting(`plan_features.${feature.key}`, v)}
                disabled={feature.comingSoon}
              />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== PORTAL VISIBILITY SECTION ====================
function PortalVisibilitySection({ settings, updateSetting, categoryColors }) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2 mb-1">
          <Eye className="w-5 h-5" /> Portal Visibility
        </h3>
        <p className="text-sm text-gray-500">Control what sections members see in their portal dashboard</p>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Visible Sections</CardTitle>
          <CardDescription>Toggle which sections appear in the member portal</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {PORTAL_SECTIONS.map(section => {
            const Icon = section.icon;
            const isEnabled = settings.visible_sections?.[section.key] !== false;
            return (
              <div 
                key={section.key} 
                className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                  isEnabled ? "bg-green-50 border-green-200" : "bg-gray-50 border-gray-200"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    isEnabled ? "bg-green-500 text-white" : "bg-gray-200 text-gray-500"
                  }`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{section.label}</p>
                    <p className="text-xs text-gray-500">{section.description}</p>
                  </div>
                </div>
                <Switch
                  checked={isEnabled}
                  onCheckedChange={(v) => updateSetting(`visible_sections.${section.key}`, v)}
                />
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Default Dashboard Layout */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Default Dashboard View</CardTitle>
          <CardDescription>What members see first when they log in</CardDescription>
        </CardHeader>
        <CardContent>
          <Select 
            value={settings.default_dashboard_view || "dashboard"} 
            onValueChange={(v) => updateSetting("default_dashboard_view", v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PORTAL_SECTIONS.filter(s => settings.visible_sections?.[s.key] !== false).map(section => (
                <SelectItem key={section.key} value={section.key}>
                  <span className="flex items-center gap-2">
                    <section.icon className="w-4 h-4" />
                    {section.label}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== PHASE TEMPLATES SECTION ====================
function PhaseTemplatesSection({ settings, updateSetting, categoryColors, subCategoryId, onPreview }) {
  const [activePhase, setActivePhase] = useState("envelope");

  const phaseSettings = settings.phase_templates || {};

  const updatePhaseField = (phase, field, value) => {
    const current = phaseSettings[phase] || {};
    updateSetting(`phase_templates.${phase}`, { ...current, [field]: value });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2 mb-1">
            <Layout className="w-5 h-5" /> Phase Templates
          </h3>
          <p className="text-sm text-gray-500">Customize each phase of the invite flow</p>
        </div>
        <Button onClick={onPreview} className={categoryColors.bg}>
          <Play className="w-4 h-4 mr-2" />
          Preview Flow
        </Button>
      </div>

      {/* Phase Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {INVITE_PHASES.map(phase => {
          const Icon = phase.icon;
          const isActive = activePhase === phase.id;
          return (
            <button
              key={phase.id}
              onClick={() => setActivePhase(phase.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border whitespace-nowrap transition-colors ${
                isActive 
                  ? `${categoryColors.bg} text-white border-transparent` 
                  : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
              }`}
            >
              <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                isActive ? "bg-white/20" : "bg-gray-200 text-gray-600"
              }`}>
                {phase.step}
              </div>
              <Icon className="w-4 h-4" />
              <span className="text-sm font-medium">{phase.label}</span>
            </button>
          );
        })}
      </div>

      {/* Phase Content Editor */}
      <Card>
        <CardContent className="pt-6 space-y-4">
          {activePhase === "envelope" && (
            <>
              <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg mb-4">
                <p className="text-sm text-purple-800">
                  <strong>✉️ Envelope Animation:</strong> Customize how the invite envelope appears. Upload images to override the default pink envelope, or leave empty for the classic look.
                </p>
              </div>
              
              {/* Default Envelope Preview */}
              <div className="mb-4">
                <Label className="text-xs mb-2 block">Preview</Label>
                <div 
                  className="h-32 rounded-xl flex flex-col items-center justify-center relative overflow-hidden"
                  style={{ 
                    backgroundColor: phaseSettings.envelope?.bg_color || categoryColors?.hex || "#ec4899",
                    backgroundImage: phaseSettings.envelope?.background_image ? `url(${phaseSettings.envelope.background_image})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                >
                  {phaseSettings.envelope?.cover_image ? (
                    <img 
                      src={phaseSettings.envelope.cover_image} 
                      alt="Cover" 
                      className="w-16 h-16 rounded-full object-cover border-4 border-white shadow-lg mb-2"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center mb-2 shadow-lg">
                      <Mail className="w-8 h-8" style={{ color: phaseSettings.envelope?.bg_color || categoryColors?.hex || "#ec4899" }} />
                    </div>
                  )}
                  <p className="text-lg font-bold tracking-widest uppercase" style={{ color: phaseSettings.envelope?.text_color || "#ffffff" }}>
                    {phaseSettings.envelope?.text || "You're Invited"}
                  </p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <ImageUpload
                  label="Background Image (optional - overrides color)"
                  value={phaseSettings.envelope?.background_image || ""}
                  onChange={(url) => updatePhaseField("envelope", "background_image", url)}
                />
                <ImageUpload
                  label="Cover Image / Logo (optional - replaces letter icon)"
                  value={phaseSettings.envelope?.cover_image || ""}
                  onChange={(url) => updatePhaseField("envelope", "cover_image", url)}
                />
              </div>
              <div>
                <Label className="text-xs">Envelope Text</Label>
                <Input
                  value={phaseSettings.envelope?.text || "You're Invited"}
                  onChange={(e) => updatePhaseField("envelope", "text", e.target.value)}
                  placeholder="You're Invited"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Background Color (used if no image)</Label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="color"
                      value={phaseSettings.envelope?.bg_color || categoryColors?.hex || "#ec4899"}
                      onChange={(e) => updatePhaseField("envelope", "bg_color", e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={phaseSettings.envelope?.bg_color || categoryColors?.hex || "#ec4899"}
                      onChange={(e) => updatePhaseField("envelope", "bg_color", e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Text Color</Label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="color"
                      value={phaseSettings.envelope?.text_color || "#ffffff"}
                      onChange={(e) => updatePhaseField("envelope", "text_color", e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={phaseSettings.envelope?.text_color || "#ffffff"}
                      onChange={(e) => updatePhaseField("envelope", "text_color", e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {activePhase === "welcome" && (
            <>
              <div>
                <Label className="text-xs">Welcome Title</Label>
                <Input
                  value={phaseSettings.welcome?.title || "Welcome to the Pilot Program"}
                  onChange={(e) => updatePhaseField("welcome", "title", e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">Subtitle</Label>
                <Input
                  value={phaseSettings.welcome?.subtitle || "You're joining an exclusive group"}
                  onChange={(e) => updatePhaseField("welcome", "subtitle", e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Textarea
                  value={phaseSettings.welcome?.description || ""}
                  onChange={(e) => updatePhaseField("welcome", "description", e.target.value)}
                  rows={3}
                  placeholder="Describe the benefits of joining..."
                />
              </div>
              <div>
                <Label className="text-xs">Benefits (one per line)</Label>
                <Textarea
                  value={(phaseSettings.welcome?.benefits || []).join("\n")}
                  onChange={(e) => updatePhaseField("welcome", "benefits", e.target.value.split("\n").filter(Boolean))}
                  rows={4}
                  placeholder="Early access to features&#10;Direct support channel&#10;Free premium tier"
                />
              </div>
              <ImageUpload
                label="Welcome Image"
                value={phaseSettings.welcome?.image || ""}
                onChange={(url) => updatePhaseField("welcome", "image", url)}
              />
            </>
          )}

          {activePhase === "nda" && (
            <>
              <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div>
                  <p className="font-medium text-sm">Require NDA Signature</p>
                  <p className="text-xs text-gray-500">Members must sign before continuing</p>
                </div>
                <Switch
                  checked={phaseSettings.nda?.required !== false}
                  onCheckedChange={(v) => updatePhaseField("nda", "required", v)}
                />
              </div>
              <div>
                <Label className="text-xs">Agreement Title</Label>
                <Input
                  value={phaseSettings.nda?.title || "Pilot Partner Agreement"}
                  onChange={(e) => updatePhaseField("nda", "title", e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">Checkbox Text</Label>
                <Input
                  value={phaseSettings.nda?.checkbox_text || "I agree to keep all pilot information confidential"}
                  onChange={(e) => updatePhaseField("nda", "checkbox_text", e.target.value)}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Show Stealth Notice</p>
                  <p className="text-xs text-gray-500">Display "stealth mode" warning</p>
                </div>
                <Switch
                  checked={phaseSettings.nda?.show_stealth !== false}
                  onCheckedChange={(v) => updatePhaseField("nda", "show_stealth", v)}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Allow PDF Download</p>
                  <p className="text-xs text-gray-500">Let members download signed NDA</p>
                </div>
                <Switch
                  checked={phaseSettings.nda?.allow_download !== false}
                  onCheckedChange={(v) => updatePhaseField("nda", "allow_download", v)}
                />
              </div>
            </>
          )}

          {activePhase === "intake" && (
            <>
              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
                <div>
                  <p className="font-medium text-sm">Require Intake Form</p>
                  <p className="text-xs text-gray-500">Members must complete before portal access</p>
                </div>
                <Switch
                  checked={phaseSettings.intake?.required !== false}
                  onCheckedChange={(v) => updatePhaseField("intake", "required", v)}
                />
              </div>
              <div>
                <Label className="text-xs">Form Title</Label>
                <Input
                  value={phaseSettings.intake?.title || "Tell Us About Your Business"}
                  onChange={(e) => updatePhaseField("intake", "title", e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">Form Subtitle</Label>
                <Input
                  value={phaseSettings.intake?.subtitle || "Help us personalize your experience"}
                  onChange={(e) => updatePhaseField("intake", "subtitle", e.target.value)}
                />
              </div>
              <div className="p-3 bg-gray-50 rounded-lg border">
                <p className="text-sm text-gray-600">
                  <ClipboardList className="w-4 h-4 inline mr-2" />
                  Configure intake questions in the <strong>Templates</strong> tab
                </p>
              </div>
            </>
          )}

          {activePhase === "portal" && (
            <>
              <div>
                <Label className="text-xs">Dashboard Welcome Message</Label>
                <Input
                  value={phaseSettings.portal?.welcome_message || "Welcome to your portal!"}
                  onChange={(e) => updatePhaseField("portal", "welcome_message", e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs">Getting Started Steps (one per line)</Label>
                <Textarea
                  value={(phaseSettings.portal?.getting_started || []).join("\n")}
                  onChange={(e) => updatePhaseField("portal", "getting_started", e.target.value.split("\n").filter(Boolean))}
                  rows={4}
                  placeholder="Complete your profile&#10;Add your portfolio&#10;Connect your calendar"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Show Onboarding Checklist</p>
                  <p className="text-xs text-gray-500">Guide members through setup</p>
                </div>
                <Switch
                  checked={phaseSettings.portal?.show_checklist !== false}
                  onCheckedChange={(v) => updatePhaseField("portal", "show_checklist", v)}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Show Updates Feed</p>
                  <p className="text-xs text-gray-500">Display admin announcements</p>
                </div>
                <Switch
                  checked={phaseSettings.portal?.show_updates !== false}
                  onCheckedChange={(v) => updatePhaseField("portal", "show_updates", v)}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== MAIN ENHANCED SETTINGS TAB ====================
export default function EnhancedSettingsTab({ subCategoryData, categoryColors, category, subCategoryId, onRefresh }) {
  const [activeSection, setActiveSection] = useState("plan");
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [platformDefaults, setPlatformDefaults] = useState({});

  const sections = [
    { id: "plan", label: "Plan & Subscription", icon: Crown },
    { id: "visibility", label: "Portal Visibility", icon: Eye },
    { id: "phases", label: "Phase Templates", icon: Layout },
    { id: "host-dashboard", label: "Host Dashboard", icon: LayoutDashboard },
    { id: "branding", label: "Branding", icon: Palette },
    { id: "toggles", label: "Feature Toggles", icon: ToggleLeft },
  ];

  useEffect(() => {
    loadSettings();
    loadPlatformDefaults();
  }, [subCategoryId]);

  const loadPlatformDefaults = async () => {
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/platform-defaults/field-options`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPlatformDefaults(data.field_options || {});
      }
    } catch (err) {
      console.error("Failed to load platform defaults:", err);
    }
  };

  const loadSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(
        `${API}/admin/portal-categories/pilots/sub-categories/${subCategoryId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setSettings(data.sub_category?.settings || {});
      }
    } catch (err) {
      console.error("Failed to load settings:", err);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("session_token");
      const res = await fetch(
        `${API}/admin/portal-categories/pilots/sub-categories/${subCategoryId}/settings`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ settings })
        }
      );
      if (res.ok) {
        toast.success("Settings saved!");
        setHasChanges(false);
        onRefresh?.();
      } else {
        toast.error("Failed to save settings");
      }
    } catch {
      toast.error("Error saving settings");
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (path, value) => {
    setSettings(prev => {
      const parts = path.split(".");
      const result = JSON.parse(JSON.stringify(prev));
      let current = result;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]]) current[parts[i]] = {};
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = value;
      return result;
    });
    setHasChanges(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-200px)]" data-testid="enhanced-settings-tab">
      {/* Left Nav */}
      <div className="w-56 border-r bg-gray-50 p-3 space-y-1 shrink-0">
        {sections.map(s => {
          const Icon = s.icon;
          return (
            <button
              key={s.id}
              onClick={() => setActiveSection(s.id)}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                activeSection === s.id
                  ? `${categoryColors.light} ${categoryColors.text} font-medium`
                  : "text-gray-600 hover:bg-gray-100"
              }`}
              data-testid={`settings-nav-${s.id}`}
            >
              <Icon className="w-4 h-4" />
              {s.label}
            </button>
          );
        })}
        
        <div className="pt-4 border-t mt-4 space-y-2">
          <Button 
            variant="outline" 
            onClick={() => setShowPreview(true)} 
            className="w-full"
            data-testid="preview-as-member-btn"
          >
            <Eye className="w-4 h-4 mr-2" />
            Preview as Member
          </Button>
          
          {hasChanges && (
            <Badge variant="secondary" className="w-full justify-center bg-amber-100 text-amber-700">
              Unsaved changes
            </Badge>
          )}
          <Button 
            onClick={handleSaveSettings} 
            disabled={saving || !hasChanges} 
            className={`w-full ${categoryColors.bg}`} 
            data-testid="save-settings-btn"
          >
            {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save All
          </Button>
        </div>
      </div>

      {/* Content */}
      <ScrollArea className="flex-1 p-6">
        <div className="max-w-2xl">
          {activeSection === "plan" && (
            <PlanSettingsSection settings={settings} updateSetting={updateSetting} categoryColors={categoryColors} />
          )}

          {activeSection === "visibility" && (
            <PortalVisibilitySection settings={settings} updateSetting={updateSetting} categoryColors={categoryColors} />
          )}

          {activeSection === "phases" && (
            <PhaseTemplatesSection 
              settings={settings} 
              updateSetting={updateSetting} 
              categoryColors={categoryColors} 
              subCategoryId={subCategoryId}
              onPreview={() => setShowPreview(true)}
            />
          )}

          {activeSection === "host-dashboard" && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold flex items-center gap-2 mb-1">
                  <LayoutDashboard className="w-5 h-5" /> Host Dashboard Template
                </h3>
                <p className="text-sm text-gray-500">Configure what hosts can edit in their live dashboards</p>
              </div>

              {/* Defaults Configuration */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ListChecks className="w-4 h-4" /> Defaults Selection
                  </CardTitle>
                  <CardDescription>Choose which default categories apply to this pilot type</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[
                    { key: "audience_types", label: "Audience Types", desc: "Who attends (builders, designers, etc.)", defaultOn: true },
                    { key: "interests", label: "Interests", desc: "Topics/interests (includes Industries sub-field)", defaultOn: true },
                    { key: "accessibility_options", label: "Accessibility", desc: "Accessibility needs options", defaultOn: true },
                    { key: "dietary_options", label: "Dietary Options", desc: "Food preferences and restrictions", defaultOn: !category?.toLowerCase().includes("instructor") },
                    { key: "vibe_options", label: "Vibe Options", desc: "Event atmosphere preferences", defaultOn: true },
                  ].map(item => {
                    // Get the saved value, or use the default for this pilot type
                    const isEnabled = settings.host_dashboard?.defaults?.[item.key] ?? item.defaultOn;
                    return (
                      <div key={item.key} className="flex items-center justify-between p-3 border rounded-lg bg-gray-50">
                        <div>
                          <p className="text-sm font-medium">{item.label}</p>
                          <p className="text-xs text-gray-500">{item.desc}</p>
                        </div>
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={(v) => updateSetting(`host_dashboard.defaults.${item.key}`, v)}
                          data-testid={`hd-default-${item.key}`}
                        />
                      </div>
                    );
                  })}
                  
                  {/* Note about Instructor Pilots */}
                  {category?.toLowerCase().includes("instructor") && (
                    <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg">
                      <p className="text-sm text-sky-800">
                        <strong>Instructor Pilot:</strong> Dietary is disabled by default. Builder Types is collected in intake.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Intake Configuration */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <ClipboardList className="w-4 h-4" /> Intake Configuration
                  </CardTitle>
                  <CardDescription>Pre-survey, question bank, and post-survey settings</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Pre-Intake / RSVP Questions */}
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-medium">Pre-Intake (RSVP) Questions</p>
                        <p className="text-xs text-gray-500">Questions asked when attendees RSVP</p>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => {
                        const current = settings.host_dashboard?.intake?.pre_questions || [];
                        updateSetting('host_dashboard.intake.pre_questions', [
                          ...current,
                          { id: `q_${Date.now()}`, question: "", type: "select", required: false, options: [] }
                        ]);
                      }}>
                        <Plus className="w-4 h-4 mr-1" /> Add Question
                      </Button>
                    </div>
                    <p className="text-xs text-gray-400 mb-2">Edit in Templates tab for full control</p>
                    <Badge variant="secondary">
                      {settings.host_dashboard?.intake?.pre_questions?.length || 0} questions configured
                    </Badge>
                  </div>

                  {/* Question Bank */}
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-medium flex items-center gap-2">
                          <Info className="w-4 h-4 text-violet-500" />
                          Question Bank
                        </p>
                        <p className="text-xs text-gray-500">Attendees can submit questions for instructors before sessions</p>
                      </div>
                      <Switch
                        checked={settings.host_dashboard?.question_bank_enabled !== false}
                        onCheckedChange={(v) => updateSetting('host_dashboard.question_bank_enabled', v)}
                      />
                    </div>
                    {settings.host_dashboard?.question_bank_enabled !== false && (
                      <div className="mt-2 p-3 bg-violet-50 rounded-lg text-sm text-violet-800">
                        Attendees will see a text field to submit questions for the instructor to address during the session.
                      </div>
                    )}
                  </div>

                  {/* Post-Survey */}
                  <div className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="font-medium">Post-Survey (Session End)</p>
                        <p className="text-xs text-gray-500">Feedback questions shown after event</p>
                      </div>
                      <Switch
                        checked={settings.host_dashboard?.intake?.post_survey_enabled !== false}
                        onCheckedChange={(v) => updateSetting('host_dashboard.intake.post_survey_enabled', v)}
                      />
                    </div>
                    {settings.host_dashboard?.intake?.post_survey_enabled !== false && (
                      <Badge variant="secondary">
                        {settings.host_dashboard?.intake?.post_questions?.length || 0} questions configured
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Section Names */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Edit2 className="w-4 h-4" /> Section Names
                  </CardTitle>
                  <CardDescription>Customize section labels for this pilot type</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[
                    { key: "overview", default: "Overview" },
                    { key: "people", default: "People" },
                    { key: "analytics", default: "Analytics" },
                    { key: "settings", default: "Settings" },
                  ].map(section => (
                    <div key={section.key} className="flex items-center gap-3">
                      <Label className="w-24 text-sm">{section.default}</Label>
                      <Input
                        placeholder={section.default}
                        value={settings.host_dashboard?.section_names?.[section.key] || ""}
                        onChange={(e) => updateSetting(`host_dashboard.section_names.${section.key}`, e.target.value)}
                        className="flex-1"
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Host Edit Permissions */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Lock className="w-4 h-4" /> Host Edit Permissions
                  </CardTitle>
                  <CardDescription>Control what hosts can modify in their dashboard</CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[
                    { key: "can_edit_defaults", label: "Edit Defaults", desc: "Hosts can modify default selections" },
                    { key: "can_edit_intake", label: "Edit Intake Questions", desc: "Hosts can add/remove intake questions" },
                    { key: "can_edit_post_survey", label: "Edit Post-Survey", desc: "Hosts can modify post-survey questions" },
                    { key: "can_edit_section_names", label: "Edit Section Names", desc: "Hosts can rename dashboard sections" },
                  ].map(perm => (
                    <div key={perm.key} className="flex items-center justify-between py-1">
                      <div>
                        <p className="text-sm font-medium">{perm.label}</p>
                        <p className="text-xs text-gray-500">{perm.desc}</p>
                      </div>
                      <Switch
                        checked={settings.host_dashboard?.permissions?.[perm.key] ?? true}
                        onCheckedChange={(v) => updateSetting(`host_dashboard.permissions.${perm.key}`, v)}
                        data-testid={`hd-perm-${perm.key}`}
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          )}

          {activeSection === "branding" && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold flex items-center gap-2 mb-1">
                  <Palette className="w-5 h-5" /> Portal Branding
                </h3>
                <p className="text-sm text-gray-500">Colors for this portal (envelope images are in Phase Templates → Envelope Animation)</p>
              </div>
              
              {/* Category Color Preview */}
              <div className={`p-4 rounded-lg border ${categoryColors.border || 'border-gray-200'}`} style={{ backgroundColor: `${categoryColors.hex || settings.brand_color || '#8B5CF6'}10` }}>
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg ${categoryColors.bg} flex items-center justify-center text-white`}>
                    <Palette className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="font-medium">Category: {category}</p>
                    <p className="text-sm text-gray-500">Primary Color: {categoryColors.hex || settings.brand_color || '#8B5CF6'}</p>
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Brand Color</Label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="color"
                      value={settings.brand_color || categoryColors.hex || "#8B5CF6"}
                      onChange={(e) => updateSetting("brand_color", e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={settings.brand_color || categoryColors.hex || "#8B5CF6"}
                      onChange={(e) => updateSetting("brand_color", e.target.value)}
                      className="flex-1"
                    />
                  </div>
                  <button 
                    onClick={() => updateSetting("brand_color", categoryColors.hex || "#8B5CF6")}
                    className="text-xs text-purple-600 hover:underline mt-1"
                  >
                    Reset to category color
                  </button>
                </div>
                <div>
                  <Label>Accent Color</Label>
                  <div className="flex gap-2 mt-1">
                    <input
                      type="color"
                      value={settings.accent_color || "#EC4899"}
                      onChange={(e) => updateSetting("accent_color", e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={settings.accent_color || "#EC4899"}
                      onChange={(e) => updateSetting("accent_color", e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>
              </div>
              
              {/* Note about envelope images */}
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>📝 Note:</strong> To change the envelope animation images (background & cover), go to <strong>Phase Templates → Envelope Animation</strong>
                </p>
              </div>
            </div>
          )}

          {activeSection === "toggles" && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold flex items-center gap-2 mb-1">
                  <ToggleLeft className="w-5 h-5" /> Feature Toggles
                </h3>
                <p className="text-sm text-gray-500">Enable or disable features for this portal</p>
              </div>
              <Card>
                <CardContent className="pt-4 space-y-4">
                  {[
                    { key: "require_intake", label: "Require Intake Form", desc: "Members must complete intake" },
                    { key: "require_nda", label: "Require NDA", desc: "NDA must be signed before access" },
                    { key: "auto_accept", label: "Auto-Accept Invites", desc: "Skip manual approval" },
                    { key: "allow_portfolio_upload", label: "Portfolio Uploads", desc: "Allow portfolio images" },
                    { key: "show_trial_badge", label: "Show Trial Badge", desc: "Display trial period badge" },
                    { key: "enable_chat", label: "Enable Chat", desc: "Allow direct messaging" },
                    { key: "enable_notifications", label: "Enable Notifications", desc: "Send email/push notifications" },
                    { key: "show_roadmap", label: "Show Roadmap", desc: "Display product roadmap" },
                  ].map(toggle => (
                    <div key={toggle.key} className="flex items-center justify-between py-1">
                      <div>
                        <p className="text-sm font-medium">{toggle.label}</p>
                        <p className="text-xs text-gray-500">{toggle.desc}</p>
                      </div>
                      <Switch
                        checked={settings.toggles?.[toggle.key] ?? true}
                        onCheckedChange={(v) => updateSetting(`toggles.${toggle.key}`, v)}
                        data-testid={`toggle-${toggle.key}`}
                      />
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Preview Modal */}
      <PreviewAsMemberModal
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        settings={settings}
        categoryColors={categoryColors}
        category={category}
        subCategoryId={subCategoryId}
      />
    </div>
  );
}
